import { create } from 'zustand';

import { useDocumentStore } from './useDocumentStore';

/**
 * Command-pattern history entry. Each entry captures both directions
 * of the operation as pre-bound closures, so the store never needs to
 * know the shape of the operation it is undoing — that keeps the
 * store generic across rotate (S-018), delete (S-019), merge (S-020),
 * annotate (S-014–S-016), form-fill (S-021–S-022), and future ops.
 *
 * Design rationale, alternatives (snapshot-based), and the 50-step
 * cap trace to [ADR-009](../../../../docs/adr/ADR-009-undo-redo-architecture.md).
 */
export interface HistoryEntry {
  /** UUID-like unique id — callers do not need to reuse it, but React
   *  keying and Sentry breadcrumb correlation want it. */
  readonly id: string;
  /** Human-readable, e.g. "Rotate page 3 by 90°". Shown in the undo
   *  menu and the command-palette redo caption. */
  readonly label: string;
  /** Forward action. Must be idempotent-safe for redo — a second
   *  invocation after undo must produce the same observable state. */
  readonly execute: () => Promise<void>;
  /** Inverse action. Must restore the pre-execute state exactly. */
  readonly undo: () => Promise<void>;
}

interface HistoryState {
  readonly past: readonly HistoryEntry[];
  readonly future: readonly HistoryEntry[];
}

interface HistoryActions {
  /** Runs `entry.execute()` and records it. Any pending redo stack
   *  is cleared (branching from the middle of history discards the
   *  tail — standard undo semantics). Calls `markDirty` on the
   *  document store after execute resolves. If execute throws, the
   *  entry is NOT recorded and the error propagates unchanged. */
  readonly push: (entry: HistoryEntry) => Promise<void>;
  /** Runs the top past entry's `undo()`, moves it to `future`.
   *  No-op when `past` is empty. When `past` becomes empty as a
   *  result, calls `clearDirty` on the document store. If undo
   *  throws, the entry stays in past and the error propagates. */
  readonly undo: () => Promise<void>;
  /** Runs the top future entry's `execute()` forward, moves it back
   *  to `past`. No-op when `future` is empty. Calls `markDirty`. */
  readonly redo: () => Promise<void>;
  /** Empties both stacks. Called on document close / reset — see
   *  `useDocumentStore.reset`. */
  readonly clear: () => void;
}

/**
 * FIFO cap. Matches C-04 §7 functional-spec "50-step history" and
 * ADR-009 §D-2. Older entries fall off the head of `past` when the
 * user keeps editing.
 */
const HISTORY_CAP = 50;

const initialState: HistoryState = {
  past: [],
  future: [],
};

export const useHistoryStore = create<HistoryState & HistoryActions>((set, get) => ({
  ...initialState,

  push: async (entry: HistoryEntry): Promise<void> => {
    await entry.execute();
    set((s) => ({
      past: [...s.past, entry].slice(-HISTORY_CAP),
      future: [],
    }));
    useDocumentStore.getState().markDirty();
  },

  undo: async (): Promise<void> => {
    const { past } = get();
    const top = past[past.length - 1];
    if (!top) {
      return;
    }
    await top.undo();
    set((s) => ({
      past: s.past.slice(0, -1),
      future: [top, ...s.future],
    }));
    if (get().past.length === 0) {
      useDocumentStore.getState().clearDirty();
    }
  },

  redo: async (): Promise<void> => {
    const { future } = get();
    const [top, ...rest] = future;
    if (!top) {
      return;
    }
    await top.execute();
    set((s) => ({
      past: [...s.past, top],
      future: rest,
    }));
    useDocumentStore.getState().markDirty();
  },

  clear: (): void => {
    set(initialState);
  },
}));
